import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { 
  Droppable, 
  Draggable, 
  DroppableProvided, 
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot
} from "@hello-pangea/dnd";
import { DepthChartPlayer } from "../../../types/DepthChartTypes";
interface PlayerListProps {
  players: DepthChartPlayer[];
  selectedSide: string;
}

export const PlayerList: React.FC<PlayerListProps> = ({
  players,
  selectedSide,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [positionFilter, setPositionFilter] = useState<string>("all");

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle position filter change
  const handlePositionFilterChange = (event: SelectChangeEvent) => {
    setPositionFilter(event.target.value);
  };

  // Filter players based on search term and position filter
  const filteredPlayers = players.filter((player) => {
    const matchesSearch =
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (player.jersey_number?.toString() || "").includes(searchTerm);

    const matchesPosition =
      positionFilter === "all" ||
      (player.position || "").toLowerCase() === positionFilter.toLowerCase();

    return matchesSearch && matchesPosition;
  });

  // Get unique positions for filter dropdown
  const uniquePositions = Array.from(
    new Set(players.map((player) => player.position).filter((pos) => pos))
  );

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 200,
        bgcolor: "#1e1e1e",
        color: "white",
        p: 2,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "calc(100vh - 220px)",
      }}
    >
      {/* Search bar */}
      <Paper
        component="form"
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
          width: "100%",
          mb: 1.5,
          bgcolor: "#2c2c2c",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1, color: "white" }}
          placeholder="Search Players"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <IconButton type="button" sx={{ p: "10px", color: "white" }}>
          <SearchIcon />
        </IconButton>
      </Paper>

      {/* Position filter */}
      <FormControl
        size="small"
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            color: "white",
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(255, 255, 255, 0.5)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "white",
            },
          },
          "& .MuiSelect-icon": {
            color: "white",
          },
        }}
      >
        <Select
          value={positionFilter}
          onChange={handlePositionFilterChange}
          displayEmpty
          sx={{ color: "white" }}
        >
          <MenuItem value="all">All Positions</MenuItem>
          <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }} />
          {uniquePositions.map((position) => (
            <MenuItem key={position} value={position || ""}>
              {position}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Player list */}
      <Droppable droppableId="playerList">
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              mt: 1,
              pr: 1,
              bgcolor: snapshot.isDraggingOver
                ? "rgba(123, 45, 58, 0.2)"
                : "transparent",
              borderRadius: 1,
              padding: snapshot.isDraggingOver ? "8px" : "4px",
              border: snapshot.isDraggingOver 
                ? "2px dashed rgba(123, 45, 58, 0.7)" 
                : "1px dashed rgba(255, 255, 255, 0.1)",
              transition: "all 0.3s ease",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                },
              },
              position: "relative",
            }}
          >
            {snapshot.isDraggingOver && (
              <Typography
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "rgba(255, 255, 255, 0.8)",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  pointerEvents: "none",
                  zIndex: 2,
                  textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                }}
              >
                Drop to remove
              </Typography>
            )}
            
            {filteredPlayers.length === 0 ? (
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  p: 2,
                  textAlign: "center",
                }}
              >
                No players found
              </Typography>
            ) : (
              filteredPlayers.map((player, index) => (
                <Draggable
                  key={`player-${player.id}`}
                  draggableId={`player-${player.id}`}
                  index={index}
                >
                  {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      elevation={snapshot.isDragging ? 6 : 1}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        bgcolor: snapshot.isDragging
                          ? "#7b2d3a"
                          : "rgba(40, 40, 40, 0.8)",
                        border: snapshot.isDragging 
                          ? "2px solid rgba(255, 255, 255, 0.5)" 
                          : "1px solid rgba(255, 255, 255, 0.1)",
                        color: "white",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderRadius: "8px",
                        transition: "all 0.15s ease",
                        transform: snapshot.isDragging ? "scale(1.05)" : "scale(1)",
                        boxShadow: snapshot.isDragging 
                          ? "0 5px 15px rgba(0,0,0,0.5)" 
                          : "none",
                        zIndex: snapshot.isDragging ? 9999 : 1,
                        opacity: snapshot.isDragging ? 0.9 : 1,
                        "&:hover": {
                          bgcolor: "rgba(60, 60, 60, 0.9)",
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          cursor: "grab",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {player.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: "rgba(255, 255, 255, 0.7)",
                            display: "flex",
                            justifyContent: "flex-start"
                          }}
                        >
                          {player.position || "No Position"}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          bgcolor: "rgba(0, 0, 0, 0.3)",
                          color: "white",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        {player.jersey_number || "-"}
                      </Typography>
                    </Paper>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Box>
  );
}; 